import { Text } from '@components/ui';
import { displayImages } from '@features/buyer/jobs/criteria-option/criteriaDisplay';
import type {
  RatingImage,
  ScalarFieldEntry,
} from '@features/buyer/jobs/criteria-option/criteriaOptionTypes';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Zoom from 'react-medium-image-zoom';

interface CriteriaMediaProps {
  typeImages: ScalarFieldEntry[];
  includeCommonImages?: boolean;
  images: RatingImage[];
  includeCriteriaNotes?: boolean;
  massage: string;
}

const sectionLabelSx: SxProps<Theme> = {
  display: 'block',
  mb: 0.75,
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
};

const PhotoGrid = ({
  title,
  photos,
}: {
  title: string;
  photos: RatingImage[];
}): JSX.Element => {
  const { t } = useTranslation();

  return (
  <Box sx={{ mt: 1.5 }}>
    <Text as="span" sx={sectionLabelSx}>
      {title}
    </Text>
    {photos.length > 0 ? (
      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
          gap: 1,
        }}
      >
        {photos.map((photo, index) => (
          <Box component="li" key={photo.id ?? photo.url ?? index}>
            <Zoom>
              <Box
                component="img"
                src={photo.url}
                alt={`${title} ${index + 1}`}
                loading="lazy"
                sx={{
                  display: 'block',
                  width: '100%',
                  aspectRatio: '4 / 3',
                  objectFit: 'cover',
                  borderRadius: 1.5,
                  bgcolor: 'action.selected',
                }}
              />
            </Zoom>
          </Box>
        ))}
      </Box>
    ) : (
      <Text tone="muted" sx={{ fontSize: 13 }}>
        {t('rating.noPhotos')}
      </Text>
    )}
  </Box>
  );
};

export function CriteriaMedia({
  typeImages,
  includeCommonImages,
  images,
  includeCriteriaNotes,
  massage,
}: CriteriaMediaProps): JSX.Element {
  const { t } = useTranslation();
  const note = massage.trim();

  return (
    <>
      {typeImages.map((item) => (
        <PhotoGrid
          key={item.id}
          title={item.ratingTitle || 'Photos'}
          photos={displayImages(item?.value?.value)}
        />
      ))}
      {includeCommonImages ? (
        <PhotoGrid title={t('rating.photos')} photos={images ?? []} />
      ) : null}
      {includeCriteriaNotes && note ? (
        <Box sx={{ mt: 1.5 }}>
          <Text as="span" sx={sectionLabelSx}>
            {t('rating.notes')}
          </Text>
          <Text sx={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
            {note}
          </Text>
        </Box>
      ) : null}
    </>
  );
}
